"use client";

import ChatCard from "@afa-shared/afa-components/dist/ChatCard";
import Heading from "@afa-shared/afa-components/dist/Heading";
import SearchInput from "@afa-shared/afa-components/dist/Inputs/SearchInput";
import Text from "@afa-shared/afa-components/dist/Text";
import PickerBox from "@components/ChatBoxComponents/PickerBox/PickerBox";
import { ContentLink } from "@components/Link/ContentLink";
import Grid from "@components/client/Grid";
import { insuranceSearchSubmitTracker } from "@containers/InsuranceHandbookSearchPage/utils/insuranceSearchTracker";
import { LandingPageType_landingpage } from "@containers/Landingpage/queries/contentfulTypes/LandingPageType";
import PageWrapper from "@containers/Layout/PageWrapper/PageWrapper";
import { defaultContext } from "@utils/contextHelper/getInitialContext";
import { shouldRemoveBottomMargin } from "@utils/layoutHelpers/layoutMarginHelper";
import { getContentAreaMaxWidth } from "@utils/pageHelper";
import { shouldUseReCapcha } from "@utils/reCapchaHelper";
import { useRouter } from "next/navigation";
import { KeyboardEvent, useState } from "react";

import { IFocusState, useFocusState } from "hooks/zustand/useFocusState";
import { FactsStructureItem } from "types/FactsStructureItem";
import { Context } from "types/context/Context";

import CFContentArea from "../../components/cFContentArea";
import pageStyles from "../../styles/page.module.css";
import extendedLandingPageStyles from "./ExtendedLandingPage.module.css";
import nonVisibleItems from "./nonVisibleItems.json";

const ExtendedLandingPage = (props: any) => {
  const setOverridePageTopFocus = useFocusState(
    (state: IFocusState) => state.setOverridePageTopFocus,
  );
  const landingPage: LandingPageType_landingpage = props?.pageData;
  const context: Context = { ...defaultContext };
  const router = useRouter();
  const useReCapcha = shouldUseReCapcha(landingPage?.mainAreaCollection?.items ?? []);
  const [inputValue, setInputValue] = useState<string>("");
  const imageData = landingPage?.ivMainImage?.[0];
  const removeLayoutBottomMargin = shouldRemoveBottomMargin(
    landingPage?.mainAreaCollection?.items ?? [],
  );

  const firstLevelItems = props?.factStructure?.structureTree?.filter(
    (item: FactsStructureItem) => !nonVisibleItems?.includes(item?.id),
  );

  const insuranceHandbookPages = props?.factStructure?.flatStructure?.filter(
    (p) => p?.slug !== "forsakringshandbok-start",
  );

  const getFilteredItems = (query: string): FactsStructureItem[] => {
    const filteredItems = insuranceHandbookPages?.filter((item: FactsStructureItem) =>
      item?.heading?.toLowerCase().includes(query?.toLowerCase()),
    );
    return filteredItems;
  };

  const handleOnClick = (value: string) => {
    const query = value ? value : "";
    const results = getFilteredItems(query?.toString())?.length || 0;
    insuranceSearchSubmitTracker(query?.toString(), results);
    setOverridePageTopFocus(true);
    router.push(`/forsakringshandboken/sok-i-handboken?query=${query}`);
  };

  const handleOnKeyUp = (value: KeyboardEvent<HTMLInputElement>) => {
    const query = value ? value : "";
    setOverridePageTopFocus(true);
    router.push(`/forsakringshandboken/sok-i-handboken?query=${query}`);
  };

  return (
    <PageWrapper
      imageData={imageData}
      topImageText={landingPage?.topImageText}
      columns={12}
      relatedContentHeading={landingPage?.relatedContentHeading}
      relatedContentCollection={landingPage?.relatedContentCollection}
      relatedContentSmallImages={landingPage?.relatedContentSmallImages}
      contentfulPreviewEntryId={landingPage.sys?.id}
      contentfulPreviewRelatedContentHeadingFieldId="relatedContentHeading"
      context={context}
      useReCapcha={useReCapcha}
      shouldRemoveBottomMargin={removeLayoutBottomMargin}
      className={extendedLandingPageStyles.extendedLandingPage}
    >
      <Grid>
        <Heading
          variant="h1"
          data-contentful-entry-id={landingPage.sys?.id}
          data-contentful-field-id="heading"
        >
          {landingPage?.heading}
        </Heading>
        {landingPage?.preamble && (
          <Text
            variant={"preamble"}
            children={landingPage?.preamble}
            data-contentful-entry-id={landingPage.sys?.id}
            data-contentful-field-id="preamble"
          />
        )}

        <div className={pageStyles.contentAreaGrid}>
          {/* CHATCARD WITH USPS */}
          {firstLevelItems?.length > 0 && (
            <ChatCard textAlign="center">
              <Heading
                variant="h2"
                as="h4"
                children={
                  "För dig som är försäkringsinformatör eller representant för arbetsgivare och fack."
                }
              ></Heading>
              <Grid
                className={extendedLandingPageStyles.itemBoxWrapper}
                container
                direction="row"
                id="1"
                padding="0.5rem"
              >
                {firstLevelItems?.map((item: FactsStructureItem, i: number) => (
                  <Grid key={i} container mobile={12} tablet={6} desktop={4} padding="0.0625rem">
                    <ContentLink
                      href={item?.url}
                      className={extendedLandingPageStyles.chatCardLink}
                      contentText={item?.name}
                    >
                      <PickerBox iconName="chevron_right" label={item?.name} clickablePickerBox />
                    </ContentLink>
                  </Grid>
                ))}
              </Grid>
              <Grid container direction="row" mobile={12} justifyContent="center" padding="0.5rem">
                <Grid direction="row" mobile={12} desktop={4} role="search">
                  <form
                    noValidate
                    className={extendedLandingPageStyles.form}
                    onSubmit={(e): void => e.preventDefault()}
                  >
                    <SearchInput
                      id="searchValue"
                      buttonAriaLabel={"Sök i försäkringshandboken"}
                      placeholder={"Sök i handboken"}
                      fullWidth
                      value={inputValue}
                      onChange={(value: string) => setInputValue(value)}
                      onKeyUp={(event: KeyboardEvent<HTMLInputElement>) => handleOnKeyUp(event)}
                      onSearchButtonClick={handleOnClick}
                      inputLabel={"Sök i försäkringshandboken"}
                    />
                  </form>
                </Grid>
              </Grid>
            </ChatCard>
          )}

          {landingPage?.mainAreaCollection?.items?.length > 0 && (
            <Grid alignItems="center">
              <Grid container>
                <CFContentArea
                  key="main-area"
                  desktopColumns={1}
                  mobileColumns={1}
                  tabletColumns={1}
                  contentAreaItems={landingPage?.mainAreaCollection?.items}
                  maxWidth={getContentAreaMaxWidth(12)}
                  context={context}
                />
              </Grid>
            </Grid>
          )}
        </div>
      </Grid>
    </PageWrapper>
  );
};

export default ExtendedLandingPage;
